import React, { useEffect, useState } from "react";
import Gantt from "dhtmlx-gantt";
import "dhtmlx-gantt/codebase/dhtmlxgantt.css";
import { getToken, base_url } from "../../config/config";

const ProjectScheduler = () => {
  const [machines, setMachines] = useState([]);
  const [projects, setProjects] = useState([]);

  // Fetch machine and project planning data
  useEffect(() => {
    fetch(`${base_url}/v1/getAllMachines`)
      .then((response) => response.json())
      .then((machineData) => {
        setMachines(machineData); // Store machine data
      });

    fetch(`${base_url}/v1/GetAllProjectPlanning`)
      .then((response) => response.json())
      .then((projectData) => {
        setProjects(projectData); // Store project planning data
      });
  }, []);

  const mapDataToGantt = () => {
    if (machines.length === 0 || projects.length === 0) return;

    let taskId = 1; // Incremental task ID
    let linkId = 1; // Incremental link ID
    const tasks = {
      data: [],
      links: [],
    };

    const machineDateMap = {};

    // Display all machines as tasks on the left side
    machines.forEach((machine, machineIndex) => {
      tasks.data.push({
        id: machineIndex + 1, // Machine IDs are incremental
        text: machine.name, // Machine name as the task label (left side)
        start_date: "", // No start date for placeholder machine tasks
        duration: "", // No duration for machine placeholders
        open: true, // Keep the tasks open for better view
        machine: machine.name, // Store machine name for reference
      });
    });

    // Map project tasks based on ANR and link processes
    projects.forEach((project) => {
      const anrGroups = groupByANR(project.data);

      Object.keys(anrGroups).forEach((anr) => {
        const anrTasks = anrGroups[anr];

        anrTasks.forEach((task, index) => {
          const machine = machines.find((m) => m.name === task.machinename); // Find the matching machine

          if (machine) {
            const taskDate = new Date(task.date).toDateString(); // Normalize date to day-level accuracy

            // Check if there's a duplicate machine name on the same date
            if (!machineDateMap[task.machinename]) {
              machineDateMap[task.machinename] = {};
            }

            if (!machineDateMap[task.machinename][taskDate]) {
              machineDateMap[task.machinename][taskDate] = [];
            }

            // Store the task in the map for future comparison
            machineDateMap[task.machinename][taskDate].push(
              taskId + machines.length,
            );

            tasks.data.push({
              id: taskId + machines.length, // Ensure unique IDs for tasks after machine list
              text: task.auftragsbezeichnung, // Task label is the task description (right side)
              start_date: new Date(task.date),
              duration: task.hours,
              anr: task.anr,
              machine: task.machinename, // Reference machine
              parent:
                machines.findIndex((m) => m.name === task.machinename) + 1, // Associate task with machine (parent)
            });

            // If it's not the first task, link it to the previous task in the same ANR group
            if (index > 0) {
              tasks.links.push({
                id: linkId++,
                source: taskId + machines.length - 1,
                target: taskId + machines.length,
                type: "0", // Finish-to-start dependency
              });
            }
            taskId++;
          }
        });
      });
    });

    // Apply different styles for tasks with or without ANR
    Gantt.templates.task_class = function (start, end, task) {
      // Check if the task should be marked red (duplicate for the same machine and date)
      const taskDate = new Date(task.start_date).toDateString();
      const isDuplicate =
        machineDateMap[task.machine] &&
        machineDateMap[task.machine][taskDate].length > 1;

      if (isDuplicate) {
        return "duplicate-task"; // Apply red class for duplicate tasks
      }

      // Add 'no-anr' class if the task doesn't have an ANR
      return task.anr ? "" : "no-anr";
    };

    // Set ANR and task description on the task bar (right side)
    Gantt.templates.task_text = function (start, end, task) {
      return task.anr ? `ANR: ${task.anr} (${task.text})` : ""; // Only show text for actual tasks
    };

    Gantt.init("gantt_here");
    Gantt.parse(tasks);
  };

  // Helper function to group tasks by ANR
  const groupByANR = (tasks) => {
    return tasks.reduce((acc, task) => {
      const anr = task.anr;
      if (!acc[anr]) {
        acc[anr] = [];
      }
      acc[anr].push(task);
      return acc;
    }, {});
  };

  useEffect(() => {
    mapDataToGantt();
  }, [machines, projects]);

  return (
    <div>
      <h2>Project Scheduler</h2>
      <div id='gantt_here' style={{ width: "100%", height: "500px" }}></div>

      {/* CSS for handling tasks without ANR and duplicate tasks */}
      <style jsx='true'>{`
        /* CSS to hide tasks without an ANR */
        .no-anr {
          display: none; /* This will hide the task completely */
        }

        /* Red background for duplicate tasks (same machinename and date) */
        .duplicate-task {
          background-color: red !important;
          color: white; /* Optional: Make text white for better readability */
        }
      `}</style>
    </div>
  );
};

export default ProjectScheduler;
